<template>
  <sp-modal
    ref="root"
    width="var(--checkout-modal-width)"
    persistent
    closeable
    expand-height-max
    no-mobile-content-padding
    :open="open"
    :modal-title="modalTitle"
    :is-hidden-temporarily="isHiddenTemporarily"
    :class="classModifiers"
    @update-open="handleModalUpdateOpen"
    @close="handleModalClose"
  >
    <sp-button
      ref="activator"
      slot="activator"
      block-sm
      class="activator"
      color="default"
      :data-testid="activatorTestId"
    >
      {{ activatorText }}
    </sp-button>

    <sp-alert v-if="error" slot="alert" type="error" variant="elevated">
      {{ error }}
    </sp-alert>

    <template v-if="stepperReady">
      <ExpiryAlert slot="content-overlay" @close="closeModal" />

      <sp-stepper
        ref="stepperElement"
        no-actions
        item-value="key"
        :items.prop="stepperItems"
        :completed.prop="completedSteps"
        :selected.prop="selectedStep"
        @update-selected="updateSelectedStep"
      >
        <sp-stepper-window-item v-for="(step, index) in stepperItems" :slot="`item-${index + 1}`" :key="step.key">
          <component
            :is="step.component"
            ref="componentRefs"
            :components="components"
            :preamble="step.preamble"
            class="stepper-item-content-component"
            :data-testid="stepTestId(step)"
            v-bind="stepPanelProps(step)"
            @goto="gotoStep"
            @scroll-to-top="scrollContentIntoView"
          >
            <slot :name="step.key" />
          </component>
        </sp-stepper-window-item>
      </sp-stepper>

      <sp-stepper-actions
        slot="actions"
        :next-test-id="TestId.events.continue"
        :disabled="disabledActions"
        :prev-action-text="prevAction.text"
        :prev-action-color="prevAction.color"
        :prev-action-variant="prevAction.variant"
        :prev-action-loading="prevActionLoading"
        :next-action-text="nextAction.text"
        :next-action-loading="nextActionLoading"
        @prev="prev"
        @next="next"
      ></sp-stepper-actions>
    </template>
    <sp-card-content-overlay slot="content-overlay" :visible="!stepperReady">
      <sp-alert v-if="hasLoadError" type="error" append-align="center" class="load-error" part="load-error">
        <span class="load-error__message">{{ resolvedLoadErrorMessage }}</span>
        <sp-button slot="append" class="load-error__retry" color="primary" @click="handleRetryLoad">
          {{ resolvedRetryActivatorText }}
        </sp-button>
      </sp-alert>
      <sp-progress v-else type="ellipsis" size="x-small"></sp-progress>
    </sp-card-content-overlay>
  </sp-modal>
</template>

<script setup>
import { useEventListener, watchImmediate } from "@vueuse/core";
import { computed, onMounted, provide, ref, useTemplateRef, watch } from "vue";
import { TestId } from "#/test-ids";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useExpose } from "#ce/composables/useExpose";
import { useFlash } from "#ce/composables/useFlash";
import { createCtaActivator } from "#ce/utils/cta";
import { toBoolean, toJSON } from "#ce/utils/props";
import { props as propsDefinitions } from "./props";
import { whenI18nReady } from "./i18n";

import ExpiryAlert from "./components/ExpiryAlert.ce.vue";
import { useCheckout } from "./composables/useCheckout";
import { useState } from "./composables/useState";
import { usePurchaseStore } from "./store/purchase";
import { useModalWatcher } from "./composables/useModalWatcher";

const emit = defineEmits(["update-open", "retry"]);

const purchaseStore = usePurchaseStore();
const { error, setError, clearError } = useState();
const { isHiddenTemporarily } = useModalWatcher();
const flash = useFlash();

const props = defineProps(propsDefinitions);

provide(
  "spCheckoutHideReviewParticipantChange",
  computed(() => toBoolean(props.hideReviewParticipantChange)),
);

provide(
  "spCheckoutHideReviewPaymentChange",
  computed(() => toBoolean(props.hideReviewPaymentChange)),
);

const {
  stepperItems,
  components,
  stepper,
  hasReview,
  selectedStep,
  completedSteps,
  StepperKeys,
  completeStep,
  resetStep,
  resetSteps,
  gotoStep,
} = useCheckout(props);

function stepPanelProps(step) {
  return step.panelProps ?? {};
}

// The payment step is the journey boundary priced checkouts stop at.
function stepTestId(step) {
  if (step.key === StepperKeys.PAYMENT) {
    return TestId.events.payment;
  }
  return undefined;
}

const open = ref(toBoolean(props.open));
const redirecting = ref(false);
const nextActionLoading = ref(false);
const prevActionLoading = ref(false);
const addToCartFailed = ref(false);

// `whenI18nReady` settles on a failed fetch too, so this gate cannot hang; the seeded English
// strings cover that case.
const i18nReady = ref(false);
whenI18nReady.then(() => (i18nReady.value = true));

const stepperReady = computed(() => purchaseStore.hasPurchaseToken && i18nReady.value);

// The loading overlay shows a retry when either the parent's resource load failed
// or our own add-to-cart request failed, so it never spins indefinitely.
const hasLoadError = computed(() => toBoolean(props.resourceError) || addToCartFailed.value);
const resolvedLoadErrorMessage = computed(
  () => props.loadErrorMessage || "Sorry, we couldn't load the registration form. Please try again.",
);
const resolvedRetryActivatorText = computed(() => props.retryActivatorText || "Try again");

const root = useTemplateRef("root");
const activator = useTemplateRef("activator");
const componentRefs = useTemplateRef("componentRefs");

// The FormPanel component is the only component that has a prev and next method
const formViewRef = computed(() => componentRefs.value?.find(({ prev, next }) => prev && next));

const { exposeProperties } = useExpose();

// ctaActivatorColorTokens carries the inline activator's color chain (see `.activator` in
// this file's styles) to the detached activator, expressed through `sp-button`'s public
// tokens. The mobile CTA bar adopts that activator into the light DOM, where this
// component's shadow styles cannot reach it.
const ctaActivatorColorTokens = Object.freeze({
  "--sp-comp-button-custom-bg-color": "var(--sp-comp-checkout-activator-bg-color, var(--sp-sys-color-warning))",
  "--sp-comp-button-custom-text-color": "var(--sp-comp-checkout-activator-text-color, var(--sp-sys-color-on-surface))",
  "--sp-comp-button-custom-hover-bg-color":
    "var(--sp-comp-checkout-activator-hover-bg-color, var(--sp-comp-checkout-activator-bg-color, var(--sp-sys-color-warning)))",
  "--sp-comp-button-custom-hover-text-color":
    "var(--sp-comp-checkout-activator-hover-text-color, var(--sp-comp-checkout-activator-text-color, var(--sp-sys-color-on-surface)))",
});

/**
 * Returns the CTA activator element
 * This is used to expose the activator button to the parent component, so it can be used to open the modal from
 * outside the component.
 */
function getCtaActivator() {
  const ctaActivator = createCtaActivator(props.mobileCtaActivatorText ?? props.activatorText, { color: "custom" });

  for (const [token, value] of Object.entries(ctaActivatorColorTokens)) {
    ctaActivator.style.setProperty(token, value);
  }

  useEventListener(ctaActivator, "click", () => (open.value = true));
  return ctaActivator;
}

exposeProperties({
  ctaActivator: {
    get: getCtaActivator,
  },
});

const stepperElement = useTemplateRef("stepperElement");

function updateSelectedStep({ detail }) {
  const [step] = detail;
  selectedStep.value = step;
}

watchImmediate(
  () => props.resource,
  (value) => purchaseStore.setResource(toJSON(value, true)),
);

const prevAction = computed(() => {
  if (selectedStep.value === 0) {
    return stepper.value.prev.firstStep;
  }
  return stepper.value.prev;
});

const nextAction = computed(() => {
  if (selectedStep.value === 0) {
    return stepper.value.next.firstStep;
  }
  if (selectedStep.value === stepperItems.value.length - 1) {
    return stepper.value.next.finalStep;
  }
  return stepper.value.next;
});

const disabledActions = computed(() => {
  if (nextActionLoading.value ?? error.value) {
    return true;
  }

  if (selectedStep.value === 0 && !toBoolean(props.rsvp)) {
    return "prev";
  }

  return false;
});

const currentStep = computed(() => stepperItems.value[selectedStep.value]);
const currentStepKey = computed(() => currentStep.value?.key);

function prev() {
  if (selectedStep.value === 0 && toBoolean(props.rsvp)) {
    return declineRsvp();
  }

  if (currentStepKey.value === StepperKeys.FORM && formViewRef.value?.prev()) {
    return;
  }

  resetStep(currentStepKey.value);

  // As we are going back we also need to reset the previous step which is going to be the current step
  // Otherwise the step will be marked as complete even though the user might have changed input values
  const previousStepKey = stepperItems.value[selectedStep.value - 1]?.key;
  resetStep(previousStepKey);

  stepperElement.value.prev();
}

async function next() {
  nextActionLoading.value = true;
  clearError();

  await transitionFrom(currentStepKey.value);
  if (redirecting.value) {
    return;
  }
  nextActionLoading.value = false;
}

async function declineRsvp() {
  const done = () => {
    flash.success("Thank you for responding.");
    closeModal();
  };

  if (props.rsvpDeclineUrl) {
    try {
      clearError();
      prevActionLoading.value = true;
      await purchaseStore.declineRsvp(props.rsvpDeclineUrl);
      done();
    } catch {
      setError("Error declining RSVP");
    } finally {
      prevActionLoading.value = false;
    }
  } else {
    done();
  }
}

function getTransition(key) {
  const transitions = {
    [StepperKeys.CART]: transitionFromCart,
    [StepperKeys.FORM]: transitionFromForm,
    [StepperKeys.PAYMENT]: transitionFromPayment,
    [StepperKeys.REVIEW]: transitionFromReview,
  };

  return transitions[key];
}

/**
 * Transitions from the current step to the next step.
 *
 * The transition is determined by the key of the current step.
 * If the transition is successful, the current step is marked as complete.
 * Otherwise, the transition is halted and the current step is not marked as complete.
 *
 * @param key {String} - The key of the current step
 * @see {@link StepperKeys}
 */
async function transitionFrom(key) {
  const transition = getTransition(key);
  const result = await transition();

  if (result !== false) {
    completeStep(key);
  }
}

async function transitionFromCart() {
  stepperElement.value.next();
}

/**
 * Transitions from the start step
 *
 * We validate the form and halt the transition if the form is invalid.
 * Next we submit the form and validate the result.
 * If the form has a payment step, we store the payment details and transition to the payment step.
 * If the form does not have a payment step, we transition to the review step.
 *
 * @returns {Boolean} Whether the transition was successful, false if the form is invalid or the submission failed
 */
async function transitionFromForm() {
  if (formViewRef.value?.next()) {
    return false;
  }

  if (!purchaseStore.isValid()) {
    return false;
  }

  const success = await purchaseStore.submitForm();
  if (!success) {
    setError("Error submitting form");
    return false;
  }

  stepperElement.value.next();
}

/**
 * Transitions from the payment step
 *
 * We validate the payment form and halt the transition if the form is invalid.
 * If a review step is required, we transition to that step.
 * If no review step is required, we confirm the payment and redirect the user to the return URL.
 */
async function transitionFromPayment() {
  clearError();

  const { error: validationError } = await purchaseStore.validatePayment();

  if (validationError) {
    console.error("Error validating payment", validationError);
    setError(validationError.message);
    return false;
  }

  if (hasReview.value) {
    stepperElement.value.next();
  } else {
    return await purchaseOrRedirect();
  }
}

/**
 * Transitions from the review step
 *
 * If payment is required, the payment will be confirmed and the user will be redirected to the return URL.
 * If no payment is required, the user will be redirected to ???.
 */
async function transitionFromReview() {
  return await purchaseOrRedirect();
}

async function purchaseOrRedirect() {
  if (purchaseStore.paymentRequired.value) {
    return await confirmPayment();
  }
  redirecting.value = true;
  window.location.href = purchaseStore.returnUrl.value;
}

async function confirmPayment() {
  const result = await purchaseStore.confirmPayment();

  if (result.error) {
    setError(result.error.message);
    return false;
  }
}

function closeModal() {
  open.value = false;
}

function handleModalClose() {
  if (!isHiddenTemporarily.value) {
    reset();
  }
}

function handleModalUpdateOpen({ detail }) {
  const [value] = detail;
  open.value = value;
}

onMounted(() => {
  addToCartIfEligible();
});

watch(open, (value) => emit("update-open", value));
watch([open, () => props.addToCartUrl], addToCartIfEligible);

async function addToCartIfEligible() {
  if (!open.value || !props.addToCartUrl) {
    return;
  }

  addToCartFailed.value = false;
  const succeeded = await purchaseStore.addToCart(props.addToCartUrl);

  // `null` means the call was skipped (already in flight); only an explicit
  // `false` marks a real failure so the overlay can offer a retry.
  if (succeeded === false) {
    addToCartFailed.value = true;
  }
}

/**
 * Retries the initial load from the error overlay: asks the parent to re-fetch
 * the resource (in case that was what failed) and re-attempts the add-to-cart.
 */
function handleRetryLoad() {
  addToCartFailed.value = false;
  emit("retry");
  addToCartIfEligible();
}

function reset() {
  purchaseStore.reset();
  clearError();
  nextActionLoading.value = false;
  redirecting.value = false;
  selectedStep.value = 0;
  resetSteps();
}

watch(currentStep, scrollContentIntoView);

function scrollContentIntoView() {
  root.value.scrollContentIntoView({
    behavior: "smooth",
    block: "start",
  });
}

const { breakpointModifiers } = useBreakpoints();
const classModifiers = computed(() => [...breakpointModifiers.value]);
</script>

<style lang="scss">
:host {
  --header-background: var(
    --sp-comp-checkout-stepper-header-background,
    var(--sp-sys-layout-hero-before-content-background, #eee)
  );
  --header-row-gap: var(--sp-ref-scale-24);
  --card-padding-inline: var(--sp-comp-card-padding-inline, 1.5rem);
  --offset-inline: calc(var(--card-padding-inline) * -1);
  --card-padding-block: var(--sp-comp-card-padding-block, 0.5rem);
  --offset-block: calc(var(--card-padding-block) * -1);
  --dialog-height: 94vh;
  --dialog-min-height: 14rem;
  --dialog-max-height: 58rem;
  --sp-comp-stepper-header-background: var(--header-background);
  --sp-comp-stepper-header-margin: var(--offset-block) -1.5rem 0 -1.5rem;
  --sp-comp-stepper-header-padding: var(--header-row-gap) var(--card-padding-inline);
  --sp-comp-stepper-header-gap: 0;
  --sp-comp-stepper-item-title-on-narrow: var(--sp-comp-checkout-stepper-title-on-narrow, none);
  --sp-comp-stepper-item-title-on-wide: var(--sp-comp-checkout-stepper-title-on-wide, block);
  --sp-comp-stepper-item-title-text-transform: var(--sp-comp-checkout-stepper-step-title-text-transform, uppercase);

  display: block;
}

sp-modal {
  --checkout-modal-width: 40rem;
  // Own typographic baseline so modal content doesn't inherit the mounting
  // context's font (e.g. an events list result card's smaller, heavier text).
  font: var(--sp-sys-typescale-body-short);
  // Form fields fill the modal width in every context.
  --sp-page-builder-form-fields-max-width: 100%;
  --sp-comp-modal-header-background-color: var(--sp-comp-checkout-modal-header-background-color);
  --sp-comp-modal-header-color: var(--sp-comp-checkout-modal-header-color);
  --sp-card-actions-padding-inline: 0 !important;
  --sp-card-actions-padding-block: 0 !important;
  --sp-comp-dialog-content-height: 100%;
  // Fills the fixed-height dialog (--dialog-height / -max-height). Both the card
  // height and max-height are 100% because max-height overrides height in CSS.
  --sp-comp-modal-card-height: 100%;
  --sp-comp-modal-card-max-height: 100%;

  &.--is-sm-screen {
    --checkout-modal-width: calc(100dvw - var(--sp-ref-scale-16));

    sp-stepper-actions::part(prev-action) {
      order: 2;
    }

    sp-stepper-actions::part(next-action) {
      order: 1;
    }
  }
}

sp-stepper-actions {
  --sp-comp-stepper-actions-gap: var(--sp-ref-scale-16);
  --sp-comp-stepper-actions-border-top: 1px solid var(--sp-sys-color-outline-variant, #ccc);
  --sp-comp-stepper-actions-box-shadow: var(--sp-sys-elevation-1);
}

sp-stepper-actions::part(prev-action),
sp-stepper-actions::part(next-action) {
  width: 100%;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.stepper-item-content-component {
  height: 100%;
  overflow-y: auto;
}

.activator {
  --bg-color: var(--sp-comp-checkout-activator-bg-color, var(--sp-sys-color-warning));
  --hover-bg-color: var(--sp-comp-checkout-activator-hover-bg-color, var(--bg-color));
  --text-color: var(--sp-comp-checkout-activator-text-color, var(--sp-sys-color-on-surface));
  --hover-text-color: var(--sp-comp-checkout-activator-hover-text-color, var(--text-color));
  --activator-min-width: var(--sp-comp-checkout-activator-width, 100%);
  --sp-comp-button-width: var(--activator-min-width);
  --sp-comp-button-min-width: var(--activator-min-width);

  display: flex;
  place-self: var(--sp-comp-checkout-activator-place-self, auto);
  min-width: var(--activator-min-width);
}

.load-error {
  max-width: 24rem;
  margin-inline: auto;
}
</style>
